import React, { useState } from 'react';
import classnames from 'classnames';
import { useRouter } from 'found';

import {
  AppBar,
  Container,
  Grid,
  List,
  ListItem,
  Drawer,
  Backdrop,
  Toolbar,
  ButtonBase,
  IconButton,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Button, Translate } from '@hydra/common/components';
import NavigationContainer from '@hydra/common/components/Header/NavigationContainer';
import NavigationDrawerContainer from '@hydra/common/components/Header/NavigationDrawerContainer';

import { useAuthenticationRoutes, useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    height: 64,
    boxShadow: 'none'
  },
  container: {
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    // Compensate 2rem units for the padding to match Front padding
    [theme.breakpoints.up('sm')]: {
      maxWidth: theme.breakpoints.values.sm + theme.spacing(4)
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: 1100 + theme.spacing(4)
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: theme.breakpoints.values.lg + theme.spacing(4)
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: theme.breakpoints.values.xl + theme.spacing(4)
    }
  },
  toolbar: {
    minHeight: 64,
    padding: 0
  },
  branding: {
    display: 'flex',
    flex: '1 0 0',
    justifyContent: 'flex-start'
  },
  brandingIcon: {
    fill: theme.palette.common.black,
    height: 24,
    width: 126,
    marginBottom: theme.spacing(0.5)
  },
  languageMenu: {
    marginRight: theme.spacing(2),
    display: 'inline-block',
    color: theme.palette.text.secondary
  },
  loginBtn: {
    marginRight: theme.spacing(1),
    '&:hover': {
      background: 'none'
    }
  },
  btn: {
    borderRadius: theme.shape.borderRadius * 2,
    // hardcoded values to match Front padding
    padding: '15px 12px',
    fontSize: '16px',
    lineHeight: '16px',
    height: 'auto',
    letterSpacing: '-0.225px'
  },
  mobileHidden: {
    [theme.breakpoints.down(1100)]: {
      display: 'none'
    }
  },
  desktopHidden: {
    [theme.breakpoints.up(1100)]: {
      display: 'none'
    }
  },
  iconButton: {
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1)
    }
  },
  menu: {
    flex: '0 1 auto',
    justifyContent: 'flex-start'
  },
  sideNavContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between'
  },
  drawer: {
    height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
    width: '100%',
    marginTop: theme.mixins.toolbar.minHeight,
    zIndex: theme.zIndex.appBar - 1,
    [theme.breakpoints.up(1100)]: {
      display: 'none'
    }
  },
  backdrop: {
    zIndex: theme.zIndex.appBar - 2,
    marginTop: theme.mixins.toolbar.minHeight,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    [theme.breakpoints.up(1100)]: {
      display: 'none'
    }
  },
  sidebarBtnContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderTop: `1px solid ${theme.palette.grey[100]}`,
    [theme.breakpoints.between('sm', 'md')]: {
      flexDirection: 'row'
    }
  }
}));

export default function NavigationBar () {
  const classes = useStyles();
  const { match: { location } } = useRouter();
  const pathnameSplit = location && location.pathname
    ? location.pathname.split('/')
    : [];
  const plan = location && location.query && location.query.plan
    ? location.query.plan
    : '';
  const isWebflowPricingPage = pathnameSplit.length && pathnameSplit[pathnameSplit.length - 1].startsWith('wf-pricing');
  const isWebflowPlan = plan ? plan.startsWith('wf') || plan.startsWith('unbound') : false;

  const showFreeTrialButton = !(isWebflowPricingPage || isWebflowPlan);
  const [openDrawer, setOpenDrawer] = useState(false);
  const { translate, locale } = useI18n();
  const handleDrawer = () => setOpenDrawer(!openDrawer);
  const { signInRoute, registerRoute } = useAuthenticationRoutes();

  const branding = (
    <div className={classes.branding}>
      <ButtonBase
        href={`/${locale}/`}
        title={`Woorank | ${translate('baseline')}`}
      >
        <Typography variant='srOnly'>
          {`Woorank | ${translate('baseline')}`}
        </Typography>
        <Icon
          className={classes.brandingIcon}
          type='logo'
          viewBox='0 0 110 20'
        />
      </ButtonBase>
      <NavigationContainer />
    </div>
  );

  const loginMenu = (
    <>
      <Button
        href={signInRoute}
        className={classnames(
          classes.btn,
          classes.loginBtn,
          classes.mobileHidden
        )}
      >
        <Translate content='log_in' />
      </Button>
      {showFreeTrialButton && (
        <Button
          variant='contained'
          color='primary'
          href={registerRoute}
          className={classnames(
            classes.btn,
            classes.mobileHidden
          )}
        >
          <Translate content='free_trial' />
        </Button>
      )}
    </>
  );

  const sideNavControl = (
    <div className={classnames(classes.menu, classes.desktopHidden)}>
      <IconButton className={classes.iconButton} onClick={handleDrawer}>
        <Icon type='menu' />
      </IconButton>
    </div>
  );

  const sideNavItems = (
    <>
      <NavigationDrawerContainer />
      <List className={classes.sidebarBtnContainer}>
        <ListItem>
          <Button
            className={classes.btn}
            href={signInRoute}
            fullWidth
          >
            <Translate content='log_in' />
          </Button>
        </ListItem>
        {showFreeTrialButton && (
          <ListItem>
            <Button
              variant='contained'
              color='primary'
              href={registerRoute}
              fullWidth
              className={classes.btn}
            >
              <Translate content='free_trial' />
            </Button>
          </ListItem>
        )}
      </List>
    </>
  );

  const mobileDrawer = (
    <>
      <Backdrop open={openDrawer} onClick={handleDrawer} className={classes.backdrop} />
      <Drawer
        variant='persistent'
        PaperProps={{
          classes: {
            root: classes.drawer
          },
          elevation: 4
        }}
        anchor='top'
        onClose={handleDrawer}
        open={openDrawer}
      >
        <div className={classes.sideNavContainer}>
          {sideNavItems}
        </div>
      </Drawer>
    </>
  );

  return (
    <>
      <AppBar className={classes.root}>
        <Container
          maxWidth={false}
          className={classes.container}
        >
          <Toolbar
            className={classes.toolbar}
          >
            <Grid
              container
              direction='row'
              justify='space-between'
              alignItems='center'
              wrap='nowrap'
            >
              {branding}
              {sideNavControl}
              <div>
                {loginMenu}
              </div>
            </Grid>
          </Toolbar>
        </Container>
      </AppBar>
      {mobileDrawer}
    </>
  );
}
